/* external */
import _, { isEmpty } from "lodash";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";

/* internal */
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";

/* relative */
import {
  CONTEXT_FIELDS,
  SYLLABUS_CONTENT_DP,
  LEARNING_STANDARD_UBD,
  LEARNING_STANDARD_PYP,
  MAX_QUESTION_GENERATION_COUNT,
} from "./AiQuestionGeneratorConstants";

/**
 * @description removing messages in conversation cache, we are adding messages only in cache from ai response
 * @param {string} aiConversationId - conversation id
 */
export const resetMessagesInAIConversations = ({ aiConversationId }) => {
  const conversation = getAIConversationsFromCache({
    id: aiConversationId,
  });

  if (!_.isEmpty(_.get(conversation, "messages.edges"))) {
    const dataToSend = update(conversation, {
      messages: {
        edges: {
          $set: [],
        },
        totalCount: {
          $set: 0,
        },
      },
    });

    writeAIConversationsInCache({
      variables: {
        id: aiConversationId,
      },
      data: {
        node: dataToSend,
      },
    });
  }
};

/**
 * @description creating intial context data from conversation metadata's localFields
 * @param {object} conversationMetadata
 * @param {string} curriculumType
 * @returns initial contextData object
 */
export const getInitialContextData = ({
  conversationMetadata,
  curriculumType,
}) => {
  const {
    CF_MYP_CONTENT,
    CF_TYPE_OF_QUESTIONS,
    CF_NUMBER_OF_QUESTIONS,
    CF_AI_INSTRUCTIONS,
    CF_SYLLABUS_CONTENT_DP,
    CF_LEARNING_STANDARD_UBD,
  } = CONTEXT_FIELDS;

  const localFields = _.get(conversationMetadata, "localFields", []);

  const initialContextData = {
    [CF_NUMBER_OF_QUESTIONS]: 0,
    [CF_TYPE_OF_QUESTIONS]: ["AUTO"],
    [CF_AI_INSTRUCTIONS]: "",
  };

  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      _.set(initialContextData, CF_MYP_CONTENT, "");
      break;

    case CURRICULUM_TYPE_DP:
      _.set(initialContextData, CF_SYLLABUS_CONTENT_DP, "");
      break;

    case CURRICULUM_TYPE_UBD:
      _.set(initialContextData, CF_LEARNING_STANDARD_UBD, "");
      break;
  }

  _.forEach(localFields, localField => {
    const key = _.get(localField, "field");
    const value = _.get(localField, "value");

    if (!_.isEmpty(key)) {
      _.set(initialContextData, key, value);

      if (
        _.isEqual(key, CF_NUMBER_OF_QUESTIONS) &&
        value > MAX_QUESTION_GENERATION_COUNT
      ) {
        _.set(initialContextData, key, MAX_QUESTION_GENERATION_COUNT);
      }
    }
  });

  return initialContextData;
};

/**
 * @description creating initial learning goals data from conversation metadata's allFields
 * @param {object} conversationMetadata
 * @param {Array} conversationMetadata
 * @returns initial learningGoalsData
 */
export const getIntialLearningGoalsData = ({
  conversationMetadata,
  learningGoalsFieldsUIDs,
}) => {
  const allFields =
    _.get(conversationMetadata, "allFields") ??
    _.get(conversationMetadata, "learningGoals") ??
    [];

  const initialLearningGoalsData = [];

  _.forEach(allFields, field => {
    const fieldUID = _.get(field, "uid");
    if (_.includes(learningGoalsFieldsUIDs, fieldUID)) {
      initialLearningGoalsData.push(field);
    }
  });

  return initialLearningGoalsData;
};

/**
 * @description updating existing fields, if not then adding it
 * @param {Array} existingFields
 * @param {Array} newFields
 * @param {string} identifier - used for comparing data
 * @returns updated fields array
 */
export const mergeFields = ({ existingFields, newFields, identifier }) => {
  // converting object's array into object with `identifier` as key
  const existingFieldsMap = _.keyBy(existingFields, identifier);
  const newFieldsMap = _.keyBy(newFields, identifier);

  const finalFieldsObj = {};
  // exist in both fields array
  const existingIdentifiers = [];

  // updating fields if exist
  _.forEach(existingFieldsMap, (value, key) => {
    if (_.has(newFieldsMap, key)) {
      _.set(finalFieldsObj, key, _.get(newFieldsMap, key));
      // used for new fields addition
      existingIdentifiers.push(key);
    } else {
      _.set(finalFieldsObj, key, value);
    }
  });

  // adding new fields
  _.forEach(newFieldsMap, (value, key) => {
    if (!_.includes(existingIdentifiers, key)) {
      _.set(finalFieldsObj, key, value);
    }
  });

  return _.values(finalFieldsObj);
};

/**
 * @description creating array for required learning goals fields UIDs
 * @param {string} curriculumType
 * @param {Array} learningGoalsFieldsUIDs
 * @returns array of required learning goals UIDs
 */
export const getRequiredFields = ({
  curriculumType,
  learningGoalsFieldsUIDs,
}) => {
  const requiredFieldsUIDs = [];

  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      requiredFieldsUIDs.push(SYLLABUS_CONTENT_DP);
      break;
    case CURRICULUM_TYPE_UBD:
      requiredFieldsUIDs.push(LEARNING_STANDARD_UBD);
      break;
    case CURRICULUM_TYPE_PYP:
      requiredFieldsUIDs.push(LEARNING_STANDARD_PYP);
      break;
  }

  // only return existed learning goals UIDs on class/unit/org
  return _.filter(requiredFieldsUIDs, fieldUID =>
    _.includes(learningGoalsFieldsUIDs, fieldUID)
  );
};

/**
 * @description get targeted content field based on curriculum type
 * @param {string} curriculumType - type of curriculum
 * @returns {string} targeted content field key
 */
export const getTargetedContentField = curriculumType => {
  const contentFieldMap = {
    [CURRICULUM_TYPE_MYP]: CONTEXT_FIELDS.CF_MYP_CONTENT,
    [CURRICULUM_TYPE_DP]: CONTEXT_FIELDS.CF_SYLLABUS_CONTENT_DP,
    [CURRICULUM_TYPE_UBD]: CONTEXT_FIELDS.CF_LEARNING_STANDARD_UBD,
    [CURRICULUM_TYPE_PYP]: CONTEXT_FIELDS.CF_LEARNING_STANDARD_PYP,
  };

  return contentFieldMap[curriculumType] || CONTEXT_FIELDS.CF_MYP_CONTENT;
};

/**
 *
 * @param {*} params
 * @returns Payload for onSendQuery for Ai Question Generator
 */
export const getOnSendQueryPayload = ({
  learningGoalsFieldsData,
  updatedAssessmentAndUnitPlanDetails,
}) => {
  return {
    // * Unit plans and learning goals
    conversationMetadata: {
      allFields: learningGoalsFieldsData,
    },
    unitPlanDetails: _.get(
      updatedAssessmentAndUnitPlanDetails,
      "updatedUnitPlanDetails",
      {}
    ),
    assessmentDetails: _.get(
      updatedAssessmentAndUnitPlanDetails,
      "updatedAssessmentDetails",
      {}
    ),

    // * Constants
    query: "",
    messages: [],
    excludedFields: [],
    entityType: "WORKSHEET",
    isUserMessageEdited: false,
    triggeredFromGenWithAIModal: true,
  };
};

/**
 *
 * @param {*} contextData
 * @returns Returning the custom instruction data
 * if empty -> ""
 * if string -> string
 * if selectedPrompt is null -> instructionData as string ( normal input case )
 * if object -> join the data with space ( Preset prompt case )
 */
export const getCustomInstructionData = contextData => {
  const instruction = _.get(contextData, CONTEXT_FIELDS.CF_AI_INSTRUCTIONS, "");

  if (!instruction || _.isEmpty(instruction)) return "";
  if (_.isString(instruction)) return instruction;

  const instructionData = _.get(instruction, "data", "");
  const selectedPrompt = _.get(instruction, "selectedPrompt", "");
  if (!selectedPrompt) return _.toString(instructionData || "");
  if (isEmpty(instructionData)) return selectedPrompt || "";

  return _.join(_.map(instructionData, "data"), " ");
};

/**
 *
 * @param {*} visualizationInput
 * @returns Sanitized visualization input
 */
export const getSanitizedVisualizationInput = visualizationInput => {
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;

  return _.replace(visualizationInput, imageRegex, (match, alt, url) => {
    try {
      const urlObj = new URL(url);
      const allowedHostnames = ["cloud.toddleapp.com", "cloud.toddleapp.cn"];
      const isValidToddleUrl = _.includes(allowedHostnames, urlObj.hostname);
      return isValidToddleUrl ? match : `![${alt}]()`;
    } catch (error) {
      return `![${alt}]()`;
    }
  });
};

/**
 * @description New AI Streaming Component for smooth and no-content-missing experience
 * @returns Boolean value indicating whether the new AI streaming for worksheets is enabled or not
 */
export const getIsNewWorksheetAIStreamingEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetGeminiMigration");
};
